/**
 * Knowledge Base Admin Page - CRUD for KB articles
 * Epic 61: Admin Kozpont Dashboard (Story 61-7)
 */

import {
  createKbArticle,
  deleteKbArticle,
  getKbArticles,
  KB_CATEGORY_LABEL_KEYS,
  KB_STATUS_LABEL_KEYS,
  updateKbArticle,
  type CreateKbArticleInput,
  type KbArticle,
  type KbArticleStatus,
  type KbCategory,
} from '@/api/admin-center';
import { Button, Card, CardContent } from '@/components/ui';
import { useApiError } from '@/hooks/use-api-error';
import { cn } from '@/lib/utils';
import { useTranslation } from '@kgc/i18n';
import { useCallback, useEffect, useState } from 'react';

// ============================================
// CONSTANTS
// ============================================

const CATEGORIES: KbCategory[] = ['BERLES', 'SZERVIZ', 'PENZUGY', 'RENDSZER', 'ALTALANOS'];
const STATUSES: KbArticleStatus[] = ['DRAFT', 'PUBLISHED', 'ARCHIVED'];

const STATUS_COLORS: Record<KbArticleStatus, string> = {
  DRAFT: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
  PUBLISHED: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
  ARCHIVED: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400',
};

const CATEGORY_COLORS: Record<KbCategory, string> = {
  BERLES: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
  SZERVIZ: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400',
  PENZUGY: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
  RENDSZER: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400',
  ALTALANOS: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300',
};

const EMPTY_FORM: CreateKbArticleInput = {
  title: '',
  content: '',
  category: 'ALTALANOS',
  tags: [],
  status: 'DRAFT',
};

// ============================================
// MAIN PAGE
// ============================================

export function KbAdminPage() {
  const { t } = useTranslation();
  const { getErrorMessage } = useApiError();
  const [articles, setArticles] = useState<KbArticle[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Form state
  const [showForm, setShowForm] = useState(false);
  const [editingArticle, setEditingArticle] = useState<KbArticle | null>(null);
  const [formData, setFormData] = useState<CreateKbArticleInput>(EMPTY_FORM);
  const [tagInput, setTagInput] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  // Delete confirmation
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Filter
  const [filterStatus, setFilterStatus] = useState<KbArticleStatus | 'ALL'>('ALL');

  const fetchArticles = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await getKbArticles({
        status: filterStatus === 'ALL' ? undefined : filterStatus,
      });
      setArticles(response.data);
    } catch (err) {
      // 49Y hibakód-batch: code-alapú lokalizált hiba (KB_ARTICLE_NOT_FOUND — #896 BE);
      // fallback a régi loadFailed-üzenetre. Kiváltja a nyers err.message-et.
      const message = getErrorMessage(err, t('error.loadFailed'));
      setError(message);
    } finally {
      setIsLoading(false);
    }
  }, [filterStatus, getErrorMessage, t]);

  useEffect(() => {
    void fetchArticles();
  }, [fetchArticles]);

  const openCreateForm = () => {
    setEditingArticle(null);
    setFormData(EMPTY_FORM);
    setTagInput('');
    setFormError(null);
    setShowForm(true);
  };

  const openEditForm = (article: KbArticle) => {
    setEditingArticle(article);
    setFormData({
      title: article.title,
      content: article.content,
      category: article.category,
      tags: [...article.tags],
      status: article.status,
    });
    setTagInput('');
    setFormError(null);
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditingArticle(null);
    setFormData(EMPTY_FORM);
    setFormError(null);
  };

  const addTag = () => {
    const tag = tagInput.trim();
    if (tag && !formData.tags.includes(tag)) {
      setFormData(prev => ({ ...prev, tags: [...prev.tags, tag] }));
      setTagInput('');
    }
  };

  const removeTag = (tagToRemove: string) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(t => t !== tagToRemove),
    }));
  };

  const handleTagKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addTag();
    }
  };

  const handleSubmit = async () => {
    if (!formData.title.trim()) {
      setFormError(t('error.required'));
      return;
    }
    if (!formData.content.trim()) {
      setFormError(t('error.required'));
      return;
    }

    setIsSubmitting(true);
    setFormError(null);
    try {
      if (editingArticle) {
        await updateKbArticle(editingArticle.id, formData);
      } else {
        await createKbArticle(formData);
      }
      closeForm();
      void fetchArticles();
    } catch (err) {
      // 49Y: code-alapú lokalizált hiba (KB_ARTICLE_NOT_FOUND — #896 BE); fallback saveFailed.
      const message = getErrorMessage(err, t('error.saveFailed'));
      setFormError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (articleId: string) => {
    setIsDeleting(true);
    try {
      await deleteKbArticle(articleId);
      setDeleteConfirmId(null);
      void fetchArticles();
    } catch (err) {
      console.error('Error deleting article:', err);
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="min-h-full kgc-bg">
      {/* Header */}
      <header className="kgc-card-bg border-b border-gray-200 dark:border-gray-700">
        <div className="mx-auto flex max-w-5xl items-center justify-between px-6 py-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{t('kb.title')}</h1>
            <p className="text-sm text-gray-500 dark:text-gray-400">{t('kb.subtitle')}</p>
          </div>
          <Button onClick={openCreateForm}>
            <svg className="mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 4v16m8-8H4"
              />
            </svg>
            {t('kb.newArticle')}
          </Button>
        </div>
      </header>

      <div className="mx-auto max-w-5xl px-6 py-6">
        {/* Status filter */}
        <div className="mb-6 flex flex-wrap gap-2">
          <button
            onClick={() => setFilterStatus('ALL')}
            className={cn(
              'rounded-lg px-4 py-2 text-sm font-medium transition-colors',
              filterStatus === 'ALL'
                ? 'bg-primary text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600'
            )}
          >
            {t('common.all')} ({articles.length})
          </button>
          {STATUSES.map(status => (
            <button
              key={status}
              onClick={() => setFilterStatus(prev => (prev === status ? 'ALL' : status))}
              className={cn(
                'rounded-lg px-4 py-2 text-sm font-medium transition-colors',
                filterStatus === status
                  ? 'bg-primary text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600'
              )}
            >
              {t(KB_STATUS_LABEL_KEYS[status] ?? status)}
            </button>
          ))}
        </div>

        {/* Loading */}
        {isLoading && (
          <div className="flex items-center justify-center py-12">
            <div className="text-gray-500 dark:text-gray-400">{t('kb.loadingArticles')}</div>
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="mb-6 rounded-lg border border-red-200 bg-red-50 p-4 dark:border-red-800 dark:bg-red-900/20">
            <p className="text-sm text-red-600 dark:text-red-400">
              {t('common.error')}: {error}
            </p>
          </div>
        )}

        {/* Articles list */}
        {!isLoading && !error && (
          <div className="space-y-3">
            {articles.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12">
                <p className="text-gray-500 dark:text-gray-400">{t('kb.noArticles')}</p>
                <Button variant="link" onClick={openCreateForm} className="mt-2">
                  {t('kb.createNewArticle')}
                </Button>
              </div>
            ) : (
              articles.map(article => (
                <Card key={article.id}>
                  <CardContent className="flex items-start justify-between p-5">
                    <div className="flex-1">
                      <div className="mb-2 flex flex-wrap items-center gap-2">
                        <span
                          className={cn(
                            'rounded-full px-2.5 py-0.5 text-xs font-medium',
                            STATUS_COLORS[article.status]
                          )}
                        >
                          {t(KB_STATUS_LABEL_KEYS[article.status] ?? article.status)}
                        </span>
                        <span
                          className={cn(
                            'rounded-full px-2.5 py-0.5 text-xs font-medium',
                            CATEGORY_COLORS[article.category]
                          )}
                        >
                          {t(KB_CATEGORY_LABEL_KEYS[article.category] ?? article.category)}
                        </span>
                        {article.tags.map(tag => (
                          <span
                            key={tag}
                            className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-500 dark:bg-gray-700 dark:text-gray-400"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                      <h3 className="font-semibold text-gray-900 dark:text-white">
                        {article.title}
                      </h3>
                      <p className="mt-1 line-clamp-2 text-sm text-gray-500 dark:text-gray-400">
                        {article.content.substring(0, 150)}
                        {article.content.length > 150 ? '...' : ''}
                      </p>
                      <div className="mt-2 flex items-center gap-4 text-xs text-gray-400 dark:text-gray-300">
                        <span>
                          {article.helpfulCount} {t('kb.helpfulCount')}
                        </span>
                        <span>
                          {t('common.updatedAt')}:{' '}
                          {new Date(article.updatedAt).toLocaleDateString('hu-HU')}
                        </span>
                      </div>
                    </div>
                    <div className="ml-4 flex flex-shrink-0 gap-2">
                      <Button variant="outline" size="sm" onClick={() => openEditForm(article)}>
                        {t('common.edit')}
                      </Button>
                      {deleteConfirmId === article.id ? (
                        <div className="flex items-center gap-1">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setDeleteConfirmId(null)}
                          >
                            {t('common.cancel')}
                          </Button>
                          <Button
                            size="sm"
                            loading={isDeleting}
                            className="bg-red-600 hover:bg-red-700"
                            onClick={() => void handleDelete(article.id)}
                          >
                            {t('common.delete')}
                          </Button>
                        </div>
                      ) : (
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20"
                          onClick={() => setDeleteConfirmId(article.id)}
                        >
                          {t('kb.deleteButton')}
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      {showForm && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="kb-article-form-title"
          onKeyDown={e => {
            if (e.key === 'Escape') closeForm();
          }}
        >
          <div className="kgc-card-bg max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-xl shadow-xl">
            {/* Modal header */}
            <div className="flex items-center justify-between border-b border-gray-200 p-6 dark:border-gray-700">
              <h2
                id="kb-article-form-title"
                className="text-xl font-semibold text-gray-900 dark:text-white"
              >
                {editingArticle ? t('kb.editArticle') : t('kb.newArticle')}
              </h2>
              <button
                onClick={closeForm}
                className="rounded-lg p-2 text-muted-foreground transition-colors hover:bg-muted hover:text-muted-foreground dark:hover:bg-muted dark:hover:text-muted-foreground"
              >
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            {/* Form */}
            <div className="space-y-4 p-6">
              {/* Title */}
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  {t('kb.articleTitle')}
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={e => setFormData(prev => ({ ...prev, title: e.target.value }))}
                  className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 text-gray-900 placeholder-gray-400 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 dark:border-gray-600 dark:bg-gray-800 dark:text-white dark:placeholder-gray-500"
                  placeholder={t('kb.titlePlaceholder')}
                />
              </div>

              {/* Content */}
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  {t('kb.content')}
                </label>
                <textarea
                  rows={10}
                  value={formData.content}
                  onChange={e => setFormData(prev => ({ ...prev, content: e.target.value }))}
                  className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 font-mono text-sm text-gray-900 placeholder-gray-400 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 dark:border-gray-600 dark:bg-gray-800 dark:text-white dark:placeholder-gray-500"
                  placeholder={t('kb.contentPlaceholder')}
                />
              </div>

              {/* Category & Status */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t('common.category')}
                  </label>
                  <select
                    value={formData.category}
                    onChange={e =>
                      setFormData(prev => ({ ...prev, category: e.target.value as KbCategory }))
                    }
                    className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 text-gray-900 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 dark:border-gray-600 dark:bg-gray-800 dark:text-white"
                  >
                    {CATEGORIES.map(cat => (
                      <option key={cat} value={cat}>
                        {t(KB_CATEGORY_LABEL_KEYS[cat] ?? cat)}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t('kb.status')}
                  </label>
                  <select
                    value={formData.status}
                    onChange={e =>
                      setFormData(prev => ({ ...prev, status: e.target.value as KbArticleStatus }))
                    }
                    className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 text-gray-900 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 dark:border-gray-600 dark:bg-gray-800 dark:text-white"
                  >
                    {STATUSES.map(status => (
                      <option key={status} value={status}>
                        {t(KB_STATUS_LABEL_KEYS[status] ?? status)}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Tags */}
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  {t('kb.tags')}
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={tagInput}
                    onChange={e => setTagInput(e.target.value)}
                    onKeyDown={handleTagKeyDown}
                    className="flex-1 rounded-lg border border-gray-300 bg-white px-4 py-2 text-gray-900 placeholder-gray-400 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 dark:border-gray-600 dark:bg-gray-800 dark:text-white dark:placeholder-gray-500"
                    placeholder={t('admin.kb.tagPlaceholder')}
                  />
                  <Button variant="outline" onClick={addTag} type="button">
                    {t('kb.addTag')}
                  </Button>
                </div>
                {formData.tags.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-2">
                    {formData.tags.map(tag => (
                      <span
                        key={tag}
                        className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2.5 py-1 text-sm text-gray-700 dark:bg-gray-700 dark:text-gray-300"
                      >
                        {tag}
                        <button
                          onClick={() => removeTag(tag)}
                          className="ml-1 text-muted-foreground hover:text-muted-foreground dark:hover:text-muted-foreground"
                        >
                          <svg
                            className="h-3 w-3"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M6 18L18 6M6 6l12 12"
                            />
                          </svg>
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {/* Form error */}
              {formError && (
                <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-600 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400">
                  {formError}
                </div>
              )}
            </div>

            {/* Modal footer */}
            <div className="flex justify-end gap-3 border-t border-gray-200 p-6 dark:border-gray-700">
              <Button variant="outline" onClick={closeForm}>
                {t('common.cancel')}
              </Button>
              <Button
                onClick={() => void handleSubmit()}
                loading={isSubmitting}
                disabled={!formData.title.trim() || !formData.content.trim()}
              >
                {editingArticle ? t('common.save') : t('common.create')}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
